import { FC } from 'react';
import { Button, Link, Tag } from '@carbon/react';
import { Checkmark, TrashCan, Undo } from '@carbon/react/icons';
import { Date } from 'components/date';
import {
  EMPTY,
  PROVENANCE_LABELS,
  awaitsReview,
  documentHeading,
  evidenceLabels,
  recordingReference,
  type VestingDocumentRow,
} from './types';
import styles from './vesting-documents.module.scss';

// One recorded-document fact, as a card: the citation, the deal it came through, the matcher's
// evidence, and the two actions a landman takes on it.

// A draft workspace's state of a row, as the server reads it.
export const VESTING_STATE_TAGS = {
  verified: ['green', 'Verified'],
  pending_add: ['teal', 'Verified (pending promote)'],
  pending_verify: ['teal', 'Pending verify'],
  unverified: ['magenta', 'Unverified'],
} as const;
export type VestingState = keyof typeof VESTING_STATE_TAGS;

// What a row's state is, as one tag. Extraction state wins over review state: a deed whose
// citation has not arrived cannot be meaningfully verified yet, and showing "Needs Verification"
// next to an empty citation is what the documentState field exists to prevent.
type StatusFields = Pick<
  VestingDocumentRow,
  'documentState' | 'verified' | 'provenance'
> & { vestingState?: VestingState };

export const vestingStatus = (
  row: StatusFields
): readonly [
  'cyan' | 'red' | 'green' | 'teal' | 'magenta' | 'gray',
  string,
] => {
  if (row.documentState === 'processing') return ['cyan', 'Processing'];
  if (row.documentState === 'failed') return ['red', 'Extraction failed'];
  if (row.verified) return VESTING_STATE_TAGS.verified;
  if (row.vestingState && row.vestingState !== 'unverified')
    return VESTING_STATE_TAGS[row.vestingState];
  if (awaitsReview(row)) return ['magenta', 'Needs Verification'];
  return ['gray', PROVENANCE_LABELS[row.provenance]];
};

export const VestingStatusTag: FC<{ row: StatusFields }> = ({ row }) => {
  const [type, label] = vestingStatus(row);
  return <Tag type={type}>{label}</Tag>;
};

// The matcher's evidence for an auto-matched row. A manual row has none — a person chose it —
// so the whole block is absent rather than rendered empty.
const Evidence: FC<{ row: VestingDocumentRow }> = ({ row }) => {
  // snake_case here, camelCase on a candidate's `reason` — evidenceLabels takes the fields so the
  // spelling is named once, at the boundary, and never downstream.
  const labels = evidenceLabels(
    row.matchDetail?.evidence,
    row.matchDetail?.interest_type_match
  );
  if (row.provenance !== 'auto' || labels.length === 0) return null;

  return (
    <div className={styles.evidence}>
      <p className={styles.evidenceHeading}>Why this was matched</p>
      <ul className={styles.evidenceList}>
        {labels.map((label) => (
          <li key={label}>{label}</li>
        ))}
      </ul>
    </div>
  );
};

interface Props {
  row: VestingDocumentRow;
  /** Hides both actions, so the card reads as a view — the Tract Summary's twin. */
  readOnly: boolean;
  /** Disables Verify while its request is open, so a second click cannot fire. */
  verifying: boolean;
  onVerify: (row: VestingDocumentRow) => void;
  onRemove: (row: VestingDocumentRow) => void;
  /** A draft workspace's verification, applied to the row on promote. */
  staged?: boolean;
  onUndoVerify?: (row: VestingDocumentRow) => void;
}

export const VestingDocumentCard: FC<Props> = ({
  row,
  readOnly,
  verifying,
  onVerify,
  onRemove,
  staged = false,
  onUndoVerify,
}) => {
  const citation = recordingReference(
    row.document.volume,
    row.document.page,
    row.document.instrumentNumber
  );

  return (
    <article
      className={`${styles.row} ${
        awaitsReview(row) ? styles.rowNeedsReview : ''
      }`}
    >
      <div className={styles.rowHeader}>
        <h4 className={styles.documentTitle}>
          <Link
            className={styles.documentLink}
            href={row.document.href}
            target="_blank"
            rel="noreferrer"
          >
            {documentHeading(row.document.documentType)}
          </Link>
          {/* No citation is the normal state of a deed still being read, and the
                    status tag already says so — an em dash would read as missing data. */}
          {citation && <span className={styles.citation}>{citation}</span>}
        </h4>
        <div className={styles.tags}>
          <VestingStatusTag row={row} />
        </div>
      </div>

      <dl className={styles.metaGrid}>
        <div className={styles.metaItem}>
          <dt>Recorded</dt>
          <dd>
            <Date
              date={row.document.recordedDate}
              format="MM/DD/YYYY"
              fallback={EMPTY}
            />
          </dd>
        </div>
        <div className={styles.metaItem}>
          <dt>County</dt>
          <dd>{row.document.county || EMPTY}</dd>
        </div>
        <div className={styles.metaItem}>
          <dt>Acquired through</dt>
          <dd>
            {row.opportunity ? (
              <Link
                className={styles.documentLink}
                href={row.opportunity.salesforceHref}
                target="_blank"
                rel="noreferrer"
              >
                {row.opportunity.name}
              </Link>
            ) : (
              EMPTY
            )}
          </dd>
        </div>
        <div className={styles.metaItem}>
          <dt>{row.verified ? 'Verified by' : 'Linked by'}</dt>
          <dd>
            {(row.verified ? row.verifiedBy : row.createdBy) ||
              PROVENANCE_LABELS[row.provenance]}
          </dd>
        </div>
      </dl>

      <Evidence row={row} />

      {!readOnly && (
        <div className={styles.rowActions}>
          {!row.verified && !staged && (
            <Button
              kind="tertiary"
              size="sm"
              renderIcon={Checkmark}
              disabled={verifying}
              onClick={() => onVerify(row)}
            >
              Verify
            </Button>
          )}
          {staged && onUndoVerify && (
            <Button
              kind="tertiary"
              size="sm"
              renderIcon={Undo}
              disabled={verifying}
              onClick={() => onUndoVerify(row)}
            >
              Undo verify
            </Button>
          )}
          <Button
            kind="ghost"
            size="sm"
            renderIcon={TrashCan}
            onClick={() => onRemove(row)}
          >
            Remove
          </Button>
        </div>
      )}
    </article>
  );
};
